// src/app/components/analytics/outcomes/outcomes-view.tsx
//
// The Outcomes view (UPM-102). Three altitudes on one truth:
//
//   1. The headline    rebased overlay, every series indexed to its own
//                      pre-move baseline, one shared axis
//   2. The working view three panes on one clock, synced crosshair, neutral
//                      gray rule-defined response windows
//   3. The proof       event study, every move aligned at week 0
//
// PRESENTATION: this view is built on the platform chart stack (Recharts), the
// same one analytics-chart.tsx uses, so the section reads as a sibling of the
// Performance charts above it rather than as an imported design mock. The old
// rule — port the mock's hand-drawn SVG verbatim — is rescinded for
// presentation: where the mock's conventions collide with the platform chart
// system, the platform system wins. Concretely, and deliberately:
//
//   • Every title, source label, legend and annotation is normal DOM. Only
//     marks, gridlines and Recharts-managed ticks live inside the chart, so
//     nothing scales with the card. (The old view drew titles into a 940-unit
//     viewBox, which magnified them on a wide screen.)
//   • Tooltips are the platform card tooltip, positioned by Recharts, so they
//     no longer clip outside the card.
//   • Axes scale to the measured data. The mock's fixed log axis to ×8 wasted
//     most of the headline chart's height on empty space above a ×1.8 peak.
//   • Colours are the platform series tokens (teal / violet / coral).
//
// WHAT IS STILL SETTLED, and stays: the three-pane structure, rebasing every
// series to its own pre-move baseline, the C/R event markers, the synced
// crosshair across the working view, the rule-defined response-window shading,
// the illustrative-data pill, and all data and copy.
//
// The recommendation series is real measurement data wherever the client has
// enough history. Each demand series is measured off a connected Google
// property, waiting when that property is connected and has not handed over a
// day yet, or illustrative when there is no connection at all, and the view says
// which in the header, on every affected pane, and in the methodology note. A
// waiting pane draws its axis and nothing else: sample data on a screen the
// Integrations page is calling Connected is the disagreement this view exists to
// make impossible.
import { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import {
  Area,
  ComposedChart,
  Line,
  LineChart,
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  fetchOutcomes,
  type OutcomesPayload,
  type SeriesPoint,
} from "@/app/services/analytics/outcomes-api";
import "./outcomes-view.css";

interface OutcomesViewProps {
  brandName?: string;
}

// ── Platform chart language ────────────────────────────────────────────────
// Series colours are the platform tokens, not the mock's palette and not hex.
// Recharts passes these straight through as SVG paint, and `var()` resolves
// there because the tokens are defined on .outcomes-viz (see the CSS).
const REC = "var(--oc-series-1)"; // recommendation — the subject of the view
const GSC = "var(--oc-series-2)"; // branded search
const GA4 = "var(--oc-series-3)"; // conversions
const TRACE = "var(--oc-trace)";  // individual event traces in the study

// Matches analytics-chart's XAxis/YAxis tick prop exactly.
const TICK = { fill: "var(--muted-foreground)", fontSize: 12, fontWeight: 500 } as const;
const GRID = "var(--border)";
const CURSOR = { stroke: "var(--muted-foreground)", strokeWidth: 1, strokeOpacity: 0.5 };
const AXIS = { axisLine: false as const, tickLine: false as const, tick: TICK };

/** Series line props, matching analytics-chart's lineProps(). */
const lineProps = (stroke: string) => ({
  type: "monotone" as const,
  stroke,
  strokeWidth: 2.5,
  strokeLinecap: "round" as const,
  connectNulls: true,
  isAnimationActive: false,
});

/** analytics-chart's measuredDot: series-colour fill, var(--card) stroke 1.5. */
const measuredDot = (color: string) => (props: any) => {
  const { cx, cy, value, index } = props;
  if (value == null || cx == null || cy == null) return <g key={`d-${index}`} />;
  return (
    <circle
      key={`d-${index}`}
      cx={cx} cy={cy} r={3}
      fill={color} stroke="var(--card)" strokeWidth={1.5}
    />
  );
};

// ── Axis helpers ───────────────────────────────────────────────────────────

/** An axis fitted to its own data, the way analytics-chart fits its Y domain,
 *  instead of a hardcoded ceiling.
 *
 *  The domain is the padded data range — NOT rounded out to the next tick — so
 *  a chart never reserves height it has no data for. Ticks are round steps
 *  (1/2/2.5/5 × 10ⁿ, whichever is closest in log space) laid out from `anchor`,
 *  the value the reader measures against: ×1 baseline on the rebased overlay,
 *  0 everywhere else. That keeps the meaningful line on a gridline and avoids
 *  ticks like "×0", which is not a thing an index can be.
 */
function fittedAxis(
  min: number,
  max: number,
  anchor: number,
  count = 4,
): { domain: [number, number]; ticks: number[] } {
  const pad = (max - min) * 0.06 || Math.abs(max) * 0.06 || 1;
  // A series that cannot go negative (a rate, a count) keeps its floor at zero.
  const lo = min >= 0 ? 0 : min - pad;
  const hi = max + pad;
  const raw = (hi - lo) / Math.max(1, count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const ladder = [1, 2, 2.5, 5, 10];
  const step = ladder.reduce((best, c) =>
    Math.abs(Math.log(norm / c)) < Math.abs(Math.log(norm / best)) ? c : best) * mag;
  const ticks: number[] = [];
  const start = Math.ceil((lo - anchor) / step) * step + anchor;
  for (let v = start; v <= hi + 1e-9; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6);
  }
  return { domain: [lo, hi], ticks };
}

/** Week ticks every 4 weeks starting at week 2, the view's own convention. */
function weekTicks(weeks: number): number[] {
  const out: number[] = [];
  for (let w = 2; w <= weeks; w += 4) out.push(w);
  return out;
}

const trim = (n: number) => String(Math.round(n * 100) / 100);
const signed = (n: number) => `${n > 0 ? "+" : ""}${n}`;

function valueAt(pts: SeriesPoint[], week: number): number | null {
  const hit = pts.find((p) => p[0] === week);
  return hit ? hit[1] : null;
}

/** Nearest recommendation read to a given week. The rec series is biweekly, so
 *  the crosshair reads the closest measured point rather than interpolating. */
function nearestRead(pts: SeriesPoint[], week: number): SeriesPoint | null {
  if (!pts.length) return null;
  return pts.reduce((best, p) =>
    Math.abs(p[0] - week) < Math.abs(best[0] - week) ? p : best,
  );
}

// ── Event markers ──────────────────────────────────────────────────────────
// Platform event style: a dashed ReferenceLine with a circular badge at the
// top, coloured by kind — the same shape analytics-chart's EventMarker draws.
// The badge carries the marker key; the full label rides in a DOM chip under
// the chart, where it can wrap and can never scale with the card.

function eventColor(key: string): string {
  // R = model refresh detected (a re-run), C = content wave live.
  return key.toUpperCase() === "R" ? "var(--infrared-coral)" : "var(--signal-blue)";
}

function EventBadge({ viewBox, evKey, color }: { viewBox?: any; evKey: string; color: string }) {
  const x = viewBox?.x ?? 0;
  const y = viewBox?.y ?? 0;
  return (
    <g transform={`translate(${x}, ${y})`} style={{ pointerEvents: "none" }}>
      <circle cx={0} cy={2} r={9} fill="var(--card)" stroke={color} strokeWidth={1.5} />
      <text
        x={0} y={2} textAnchor="middle" dominantBaseline="central"
        fontSize={10} fontWeight={700} fill={color}
      >
        {evKey}
      </text>
    </g>
  );
}

/** The event reference lines for one pane. `badges` draws the key markers —
 *  only the top pane of a stack shows them, so a shared clock does not stack
 *  three identical badges down the column. */
function eventLines(
  events: OutcomesPayload["events"],
  badges: boolean,
) {
  return events.map((e) => {
    const color = eventColor(e.key);
    return (
      <ReferenceLine
        key={`${e.key}-${e.week}`}
        x={e.week}
        stroke={color}
        strokeDasharray="4 4"
        strokeOpacity={0.6}
        label={badges
          ? ((props: any) => <EventBadge viewBox={props.viewBox} evKey={e.key} color={color} />) as any
          : undefined}
      />
    );
  });
}

// ── Tooltip ────────────────────────────────────────────────────────────────
// The platform card tooltip from analytics-chart's CustomTooltip. Recharts
// positions it, so it stays inside the card instead of floating out of it.

/** The x value under the cursor.
 *
 *  Read it off the hovered row rather than the `label` prop: the stacked panes
 *  in the working view hide every XAxis but the last one, and a hidden axis
 *  hands the tooltip no label, which silently collapsed the whole readout. */
function activeX(props: any, key: string): number | null {
  const fromRow = props?.payload?.[0]?.payload?.[key];
  if (typeof fromRow === "number") return fromRow;
  const n = Number(props?.label);
  return Number.isFinite(n) ? n : null;
}

function TooltipCard({ title, rows }: {
  title: string;
  rows: Array<{ color: string; text: string }>;
}) {
  if (!rows.length) return null;
  return (
    <div className="oc-tt">
      <p className="oc-tt-title">{title}</p>
      {rows.map((r, i) => (
        <div className="oc-tt-row" key={i}>
          <span className="oc-tt-dot" style={{ background: r.color }} />
          <span className="oc-tt-text">{r.text}</span>
        </div>
      ))}
    </div>
  );
}

// ── DOM chrome ─────────────────────────────────────────────────────────────
// Titles, source labels and legends are DOM, at a fixed type scale.

function PaneHead({ title, src }: { title: string; src?: string }) {
  return (
    <div className="oc-pane-head">
      <span className="oc-pane-title">{title}</span>
      {src && <span className="oc-pane-src">{src}</span>}
    </div>
  );
}

type Chip = { color: string; label: string; swatch?: "line" | "band" };

function Legend({ items }: { items: Chip[] }) {
  return (
    <div className="oc-legend">
      {items.map((c) => (
        <span key={c.label}>
          <i
            className={c.swatch === "band" ? "oc-swatch-band" : undefined}
            style={{ background: c.color }}
          />
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ── View 1: rebased overlay ────────────────────────────────────────────────

function RebasedOverlay({ data }: { data: OutcomesPayload }) {
  const weeks = data.weeks;

  // The rebased recommendation series is kept as points as well as rows: it is
  // biweekly, so the tooltip reads the NEAREST measured point rather than
  // interpolating or dropping the row on an unmeasured week.
  const recPts = useMemo(() => {
    const base = data.recommendation.baseline;
    return base
      ? data.recommendation.points.map((p) => [p[0], p[1] / base] as SeriesPoint)
      : [];
  }, [data.recommendation]);

  const rows = useMemo(() => {
    // A waiting series has no baseline and no points, so it rebases to an empty
    // map and the overlay ignores it exactly as it ignores an absent series.
    const rebase = (pts: SeriesPoint[], base: number | null) =>
      base ? new Map(pts.map((p) => [p[0], p[1] / base])) : new Map<number, number>();
    const rec = new Map(recPts.map((p) => [p[0], p[1]]));
    const gsc = rebase(data.brandedSearch.points, data.brandedSearch.baseline);
    const ga4 = rebase(data.conversions.points, data.conversions.baseline);
    const out: Array<Record<string, number | null>> = [];
    for (let w = 1; w <= weeks; w++) {
      out.push({
        week: w,
        rec: rec.get(w) ?? null,
        gsc: gsc.get(w) ?? null,
        ga4: ga4.get(w) ?? null,
      });
    }
    return out;
  }, [data, weeks, recPts]);

  // Fit the axis to the data. The mock reserved a log scale up to ×8; a client
  // peaking at ×1.8 got most of the pane as empty headroom. Baseline (×1) is
  // always in view because it is what every series is indexed to.
  const { ticks, domain } = useMemo(() => {
    const vals = rows.flatMap((r) =>
      [r.rec, r.gsc, r.ga4].filter((v): v is number => v != null));
    // Ticks step off ×1 — every series is indexed to it, so it is the line the
    // reader actually measures against.
    return fittedAxis(Math.min(1, ...vals), Math.max(1, ...vals), 1, 5);
  }, [rows]);

  return (
    <div className="oc-card">
      {/* Chips are derived from what this chart actually draws. A waiting series
          has no points, so it gets no line and no chip: a legend entry for a
          line that is not there is a claim stated in prose and checked never. */}
      <Legend items={[
        ...(data.recommendation.points.length
          ? [{ color: REC, label: "Recommendation share, Upmode" }] : []),
        ...(data.brandedSearch.points.length
          ? [{ color: GSC, label: "Branded search, Search Console" }] : []),
        ...(data.conversions.points.length
          ? [{ color: GA4, label: "Conversions, GA4" }] : []),
      ]} />
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows} margin={{ top: 18, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={GRID} strokeOpacity={0.6} vertical={false} />
          <XAxis
            dataKey="week" type="number" domain={[1, weeks]}
            ticks={weekTicks(weeks)} tickFormatter={(w) => `wk ${w}`}
            {...AXIS}
          />
          <YAxis
            domain={domain} ticks={ticks} width={52}
            tickFormatter={(v) => `×${trim(v)}`}
            {...AXIS}
          />
          <ReferenceLine y={1} stroke={GRID} />
          {eventLines(data.events, true)}
          <Tooltip
            cursor={CURSOR}
            content={(props: any) => {
              if (!props?.active) return null;
              const week = activeX(props, "week");
              const row = week == null ? null : rows[week - 1];
              if (!row || week == null) return null;
              const rows_: Array<{ color: string; text: string }> = [];
              const read = nearestRead(recPts, week);
              if (read) rows_.push({ color: REC, text: `Recommendation ×${read[1].toFixed(1)}` });
              if (row.gsc != null) rows_.push({ color: GSC, text: `Branded search ×${row.gsc.toFixed(2)}` });
              if (row.ga4 != null) rows_.push({ color: GA4, text: `Conversions ×${row.ga4.toFixed(2)}` });
              return <TooltipCard title={`week ${week}`} rows={rows_} />;
            }}
          />
          <Line {...lineProps(REC)} dataKey="rec" dot={measuredDot(REC)} activeDot={{ r: 5, strokeWidth: 0 }} />
          <Line {...lineProps(GSC)} dataKey="gsc" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
          <Line {...lineProps(GA4)} dataKey="ga4" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
        </LineChart>
      </ResponsiveContainer>
      <EventKey events={data.events} />
    </div>
  );
}

/** The marker legend: the full event copy, in DOM, where it wraps instead of
 *  overrunning the plot the way the mock's in-chart labels did. */
function EventKey({ events }: { events: OutcomesPayload["events"] }) {
  if (!events.length) return null;
  const seen = new Set<string>();
  const uniq = events.filter((e) => (seen.has(e.key) ? false : (seen.add(e.key), true)));
  return (
    <div className="oc-eventkey">
      {uniq.map((e) => (
        <span key={e.key}>
          <b style={{ color: eventColor(e.key), borderColor: eventColor(e.key) }}>{e.key}</b>
          {e.label}
        </span>
      ))}
    </div>
  );
}

// ── View 2: working view, one clock, synced crosshair ──────────────────────

/** A demand pane's source string, DERIVED from that series' own `source` rather
 *  than hardcoded per pane. A connected Search Console or GA4 property makes the
 *  series measured; a connected property with no day read yet is waiting, which
 *  is an absence with a reason and not sample data; without a connection it is
 *  sample data and the string says so. The two panes are independent: one can be
 *  measured while the other is not.
 *
 *  A measured line names the property the figure was read from, taken from the
 *  payload rather than typed here: which GA4 property or which Search Console
 *  site is a fact about this client's connection, and a source line that named
 *  one from the component would be naming a property nobody checked. The segment
 *  is dropped when the payload has no name to give. */
function demandSrc(series: OutcomesPayload["brandedSearch"], instrument: string) {
  if (series.source === "waiting") return `${instrument} · waiting for the first read`;
  if (series.source !== "measurement") return `${instrument} · weekly · illustrative`;
  return series.property
    ? `${instrument} · ${series.property} · weekly`
    : `${instrument} · weekly`;
}

function TerminalStack({ data }: { data: OutcomesPayload }) {
  const weeks = data.weeks;
  const measured = data.recommendation.source === "measurement";

  const rows = useMemo(() => {
    const out: Array<Record<string, number | null>> = [];
    for (let w = 1; w <= weeks; w++) {
      out.push({
        week: w,
        rec: valueAt(data.recommendation.points, w),
        gsc: valueAt(data.brandedSearch.points, w),
        ga4: valueAt(data.conversions.points, w),
        // Dense zero series — see the tooltip anchor in pane 1 below. It sits
        // on the axis floor so it cannot affect any pane's fitted domain.
        anchor: 0,
      });
    }
    return out;
  }, [data, weeks]);

  // The "no noise range yet" note lives in the pane header, not in the plot.
  const recSrc = measured ? "Upmode measurement runs" : "Upmode locked instrument · biweekly · illustrative";
  const panes = [
    {
      key: "rec" as const,
      title: "Primary recommendation share",
      src: data.noiseBand ? recSrc : `${recSrc} · noise range not established yet`,
      color: REC,
      h: 176,
      fmt: (v: number) => `${trim(v)}%`,
    },
    {
      key: "gsc" as const,
      title: "Branded search impressions",
      src: demandSrc(data.brandedSearch, "Search Console"),
      color: GSC,
      h: 148,
      fmt: (v: number) => trim(v),
    },
    {
      key: "ga4" as const,
      title: "Conversions",
      src: demandSrc(data.conversions, "GA4"),
      color: GA4,
      h: 148,
      fmt: (v: number) => trim(v),
    },
  ];

  // Every pane fits its own axis to its own data, the way analytics-chart does,
  // rather than the mock's fixed 6% / 640 / 19 ceilings.
  const axisFor = (key: "rec" | "gsc" | "ga4") => {
    const vals = rows.map((r) => r[key]).filter((v): v is number => v != null);
    const hi = vals.length ? Math.max(...vals) : 1;
    const band = key === "rec" && data.noiseBand ? data.noiseBand.hi : 0;
    return fittedAxis(0, Math.max(hi, band), 0, 4);
  };

  const recRing = data.noiseBand?.exitWeek ?? null;
  // The exit read keeps its ring marker; the wording that used to sit beside it
  // in the plot moves into the pane's annotation chips below.
  const recDot = (props: any) => {
    const { cx, cy, value, index, payload } = props;
    if (value == null || cx == null || cy == null) return <g key={`d-${index}`} />;
    const ring = payload?.week === recRing;
    return (
      <g key={`d-${index}`}>
        <circle cx={cx} cy={cy} r={ring ? 4.5 : 3} fill={REC} stroke="var(--card)" strokeWidth={1.5} />
        {ring && <circle cx={cx} cy={cy} r={8.5} fill="none" stroke={REC} strokeWidth={1.5} />}
      </g>
    );
  };

  const workingTooltip = (props: any) => {
    if (!props?.active) return null;
    const week = activeX(props, "week");
    const row = week == null ? null : rows[week - 1];
    if (!row || week == null) return null;
    const read = nearestRead(data.recommendation.points, week);
    const out: Array<{ color: string; text: string }> = [];
    if (read) out.push({ color: REC, text: `${read[1].toFixed(1)}% primary rec (read wk ${read[0]})` });
    if (row.gsc != null) out.push({ color: GSC, text: `${row.gsc} branded impressions` });
    if (row.ga4 != null) out.push({ color: GA4, text: `${row.ga4} conversions` });
    return <TooltipCard title={`week ${week}`} rows={out} />;
  };

  return (
    <div className="oc-card">
      {panes.map((p, i) => {
        const { ticks, domain } = axisFor(p.key);
        const last = i === panes.length - 1;
        return (
          <div className="oc-pane" key={p.key}>
            <PaneHead title={p.title} src={p.src} />
            {i === 0 && (
              <Legend items={[
                ...(data.noiseBand
                  ? [{ color: "var(--oc-band)", label: "measured noise range", swatch: "band" as const }]
                  : []),
                ...(recRing != null
                  ? [{ color: REC, label: "first read outside the band" }]
                  : []),
              ]} />
            )}
            <ResponsiveContainer width="100%" height={p.h + (last ? 22 : 0)}>
              <LineChart
                data={rows}
                syncId="oc-working"
                margin={{ top: 14, right: 16, bottom: last ? 4 : 0, left: 0 }}
              >
                <CartesianGrid stroke={GRID} strokeOpacity={0.6} vertical={false} />
                <XAxis
                  dataKey="week" type="number" domain={[1, weeks]}
                  ticks={weekTicks(weeks)} tickFormatter={(w) => `wk ${w}`}
                  hide={!last}
                  {...AXIS}
                />
                <YAxis
                  domain={domain} ticks={ticks} width={52}
                  tickFormatter={p.fmt}
                  {...AXIS}
                />

                {/* Response windows: neutral bands, defined by rule and never
                    by eye. Repeated per pane so the shading reads as one band
                    running down the whole stack. */}
                {data.responseWindows.map((w) => (
                  <ReferenceArea
                    key={`${w.from}-${w.to}`}
                    x1={w.from} x2={w.to}
                    fill={GRID} fillOpacity={0.35} stroke="none"
                  />
                ))}

                {/* Noise band on pane 1, when the instrument has one. */}
                {i === 0 && data.noiseBand && (
                  <ReferenceArea
                    y1={data.noiseBand.lo} y2={data.noiseBand.hi}
                    fill="var(--oc-band)" fillOpacity={1} stroke="none"
                  />
                )}

                {eventLines(data.events, i === 0)}

                <Tooltip
                  cursor={CURSOR}
                  content={i === 0 ? workingTooltip : (() => null) as any}
                  wrapperStyle={i === 0 ? undefined : { display: "none" }}
                />
                <Line
                  {...lineProps(p.color)}
                  dataKey={p.key}
                  dot={p.key === "rec" ? recDot : false}
                  activeDot={{ r: 5, strokeWidth: 0 }}
                />
                {/* Tooltip anchor, pane 1 only. The recommendation series is
                    biweekly: on an unmeasured week this pane has no payload and
                    Recharts hides the readout even though the crosshair is
                    live, which silently drops the combined tooltip on every
                    other week. A dense invisible series pinned to the axis
                    floor keeps the payload populated without touching the
                    fitted domain. Nothing reads its values — the tooltip takes
                    every number from `rows`. */}
                {i === 0 && (
                  <Line
                    dataKey="anchor" stroke="none" dot={false} activeDot={false}
                    isAnimationActive={false} legendType="none"
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      })}
      <EventKey events={data.events} />
    </div>
  );
}

// ── View 3: event study ────────────────────────────────────────────────────

function EventStudy({ data }: { data: OutcomesPayload }) {
  const ev = data.eventStudy.series;
  const T = ev[0]?.length ?? 0;

  const rows = useMemo(() => {
    const out: Array<Record<string, any>> = [];
    for (let t = 0; t < T; t++) {
      const vals = ev.map((e) => e[t]);
      const m = vals.reduce((x, y) => x + y, 0) / (vals.length || 1);
      const sd = Math.sqrt(vals.reduce((x, y) => x + (y - m) * (y - m), 0) / (vals.length || 1));
      const row: Record<string, any> = {
        t: t + data.eventStudy.tMin,
        avg: m,
        band: [m - sd, m + sd],
      };
      ev.forEach((e, i) => { row[`ev${i}`] = e[t]; });
      out.push(row);
    }
    return out;
  }, [ev, T, data.eventStudy.tMin]);

  const { ticks, domain } = useMemo(() => {
    const vals = rows.flatMap((r) => [r.band[0], r.band[1], r.avg, ...ev.map((_, i) => r[`ev${i}`])]);
    // Anchored at 0 — the pre-event level every trace is plotted relative to.
    return fittedAxis(Math.min(0, ...vals), Math.max(0, ...vals), 0, 4);
  }, [rows, ev]);

  if (!T) return null;

  const tTicks = rows.filter((_, i) => i % 2 === 0).map((r) => r.t);

  return (
    <div className="oc-card">
      <Legend items={[
        { color: GSC, label: "Average branded search response" },
        { color: TRACE, label: `Individual events (n = ${ev.length}, ${data.eventStudy.clients} clients, illustrative)` },
      ]} />
      <ResponsiveContainer width="100%" height={290}>
        <ComposedChart data={rows} margin={{ top: 18, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={GRID} strokeOpacity={0.6} vertical={false} />
          <XAxis
            dataKey="t" type="number" domain={["dataMin", "dataMax"]}
            ticks={tTicks} tickFormatter={(t) => signed(t)}
            {...AXIS}
          />
          <YAxis
            domain={domain} ticks={ticks} width={52}
            tickFormatter={(v) => `${v > 0 ? "+" : ""}${trim(v)}%`}
            {...AXIS}
          />
          <ReferenceLine y={0} stroke={GRID} />
          <ReferenceLine
            x={0}
            stroke="var(--muted-foreground)" strokeDasharray="4 4" strokeOpacity={0.6}
            label={{
              value: "wk 0 · recommendation exits the noise band",
              position: "insideTopLeft",
              fontSize: 11,
              fill: "var(--muted-foreground)",
            } as any}
          />
          <Tooltip
            cursor={CURSOR}
            content={(props: any) => {
              if (!props?.active) return null;
              const t = activeX(props, "t");
              const row = t == null ? null : rows.find((r) => r.t === t);
              if (!row) return null;
              return (
                <TooltipCard
                  title={`week ${signed(row.t)}`}
                  rows={[{ color: GSC, text: `avg ${row.avg > 0 ? "+" : ""}${Math.round(row.avg)}% branded search` }]}
                />
              );
            }}
          />
          <Area
            dataKey="band" stroke="none" fill="var(--oc-studyband)" fillOpacity={1}
            isAnimationActive={false} activeDot={false}
          />
          {ev.map((_, i) => (
            <Line
              key={i} type="monotone" dataKey={`ev${i}`}
              stroke={TRACE} strokeWidth={1.25} dot={false} activeDot={false}
              isAnimationActive={false}
            />
          ))}
          <Line {...lineProps(GSC)} dataKey="avg" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
        </ComposedChart>
      </ResponsiveContainer>
      <p className="oc-axis-title">weeks relative to the recommendation move</p>
    </div>
  );
}

// ── Quiet copy primitives ──────────────────────────────────────────────────
// Teaching copy moves off the page and behind these. The charts carry the
// argument; the prose is available on demand rather than set as body text.

function InfoTip({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="oc-info">
      <button
        type="button"
        className="oc-info-btn"
        aria-label="What this shows"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Info size={13} />
      </button>
      {open && <span className="oc-info-pop">{children}</span>}
    </span>
  );
}

function InfoAffordance({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="oc-info">
      <button type="button" className="oc-method-btn" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <Info size={13} />
        {label}
      </button>
      {open && <span className="oc-info-pop oc-info-pop--wide">{children}</span>}
    </span>
  );
}

// ── The view ───────────────────────────────────────────────────────────────

export function OutcomesView({ brandName }: OutcomesViewProps) {
  const [data, setData] = useState<OutcomesPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const brand = (brandName || "").trim();
    if (!brand) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setFailed(false);
    fetchOutcomes(brand)
      .then((res) => { if (alive) setData(res); })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [brandName]);

  if (loading || failed || !data) {
    return (
      <div className="outcomes-viz">
        <div className="oc-card" style={{ minHeight: 120 }}>
          <p style={{ color: "var(--oc-ink-2)", fontSize: 13 }}>
            {failed ? "Outcomes could not be loaded." : "Loading Outcomes…"}
          </p>
        </div>
      </div>
    );
  }

  const measured = data.recommendation.source === "measurement";

  return (
    <div className="outcomes-viz" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Section header, matching the rest of Performance. The concept-deck
          hero is gone: this is a section of a product page, not a pitch. */}
      <div className="oc-section-head">
        <div>
          <h3 className="oc-h">Outcomes</h3>
          <p className="oc-lede">
            When AI recommendation moves, demand follows.
          </p>
        </div>
        <div className="oc-head-right">
          {/* One pill, one slot. The illustrative pill follows the payload flag,
              which is true whenever ANY of the three series is still sample
              data. It names WHICH, so a client with Search Console connected and
              GA4 not is not told the whole view is sample data.
              When nothing is illustrative and a connected property is still
              waiting for its first day, the slot carries "Waiting for data"
              instead. The two can never show at once: illustrative wins the
              slot, and the methodology note carries the waiting sentence. The
              markers and the event study are always illustrative and carry their
              own labels in their own blocks and in the methodology note, which is
              never suppressed. */}
          {data.illustrative ? (
            <span className="oc-tag">{illustrativePill(data)}</span>
          ) : data.waiting ? (
            <span className="oc-tag">Waiting for data</span>
          ) : null}
          <InfoAffordance label="How this is measured">
            {methodology(data, measured)}
          </InfoAffordance>
        </div>
      </div>

      <section className="oc-block">
        <h4 className="oc-block-h">
          The headline
          <InfoTip>
            Every series indexed to its own pre-move baseline = {"×"}1, the growth of $100
            convention. One shared axis, scaled to the measured range so the movement is
            legible.
          </InfoTip>
        </h4>
        <RebasedOverlay data={data} />
      </section>

      <section className="oc-block">
        <h4 className="oc-block-h">
          The working view
          <InfoTip>
            Recommendation and demand on one clock. Hover anywhere: one crosshair runs
            through all three panes. Gray bands are response windows, a fixed rule of weeks
            +1 through +5 after any read exits the noise band, applied to every move.
          </InfoTip>
        </h4>
        <TerminalStack data={data} />
      </section>

      <section className="oc-block">
        <h4 className="oc-block-h">
          The proof
          <InfoTip>
            Every time recommendation share exited the noise band, aligned at week 0, with
            branded demand plotted relative to its pre-event level. Thin lines are individual
            events; the bold line is the average.
          </InfoTip>
        </h4>
        <EventStudy data={data} />
      </section>
    </div>
  );
}

/** Which series are still sample data, named rather than generalised. A client
 *  with Search Console connected and GA4 not must not be told the whole view is
 *  illustrative.
 *
 *  Read off each series being illustrative rather than off it not being
 *  measured. A waiting series is neither, and calling it illustrative would name
 *  a series in this pill that is not sample data at all. */
function illustrativePill(data: OutcomesPayload) {
  const recSample = data.recommendation.source === "illustrative";
  const gscSample = data.brandedSearch.source === "illustrative";
  const ga4Sample = data.conversions.source === "illustrative";

  if (!recSample && gscSample && ga4Sample) return "Illustrative demand data";
  if (!recSample && gscSample) return "Illustrative branded search data";
  if (!recSample && ga4Sample) return "Illustrative conversions data";
  if (recSample && !gscSample && !ga4Sample) return "Illustrative recommendation data";
  return "Illustrative data";
}

/** The methodology note, behind an info affordance rather than set as a
 *  paragraph of body copy under the charts. */
function methodology(data: OutcomesPayload, measured: boolean) {
  const gscMeasured = data.brandedSearch.source === "measurement";
  const ga4Measured = data.conversions.source === "measurement";
  const gscWaiting = data.brandedSearch.source === "waiting";
  const ga4Waiting = data.conversions.source === "waiting";
  // Named per series, from each series' own source, because a sentence that
  // asserts what is and is not ingested and is never checked against the payload
  // is how a view ends up claiming the opposite of what it is drawing. A waiting
  // series is listed as neither measured nor sample: it is connected, and the
  // waiting sentence below is the one that names it.
  const sample = [
    data.brandedSearch.source === "illustrative" ? "branded search" : null,
    data.conversions.source === "illustrative" ? "conversions" : null,
    "the markers",
    "the event study",
  ].filter(Boolean) as string[];
  const sampleList = sample.length > 1
    ? `${sample.slice(0, -1).join(", ")} and ${sample[sample.length - 1]}`
    : sample[0];

  return (
    <>
      <p>
        {measured
          ? `Recommendation share is measured: primary recommendation rate from ${data.brand}'s measurement runs, blended across the models in that client's baseline, the same numbers behind the Brand Monitoring headline.`
          : `${data.brand} does not yet have enough measurement runs to plot a recommendation series, so the recommendation line is sample data too.`}
      </p>
      {(gscMeasured || ga4Measured) && (
        <p>
          {gscMeasured && ga4Measured
            ? `Branded search and conversions are measured: read from ${data.brand}'s own Search Console and Google Analytics properties, weekly, read only. Branded search counts queries containing this client's brand terms, and it is a floor rather than a total, because Google drops rare queries from query level data before we see them.`
            : gscMeasured
              ? `Branded search is measured: read from ${data.brand}'s own Search Console property, weekly, read only. It counts queries containing this client's brand terms, and it is a floor rather than a total, because Google drops rare queries from query level data before we see them.`
              : `Conversions are measured: key events read from ${data.brand}'s own Google Analytics property, weekly, read only.`}
        </p>
      )}
      {(gscWaiting || ga4Waiting) && (
        <p>
          {gscWaiting && ga4Waiting
            ? "Branded search and conversions are connected and waiting for the first days of data from Google."
            : gscWaiting
              ? "Branded search is connected and waiting for the first days of data from Search Console."
              : "Conversions are connected and waiting for the first days of data from Google Analytics."}
        </p>
      )}
      <p>
        {sampleList.charAt(0).toUpperCase() + sampleList.slice(1)}{" "}
        {sample.length > 1 ? "are" : "is"} illustrative, shaped like a real
        engagement but not this client.
      </p>
      <p>
        Recommendation share is measured on a fixed battery of persona built buyer questions
        across all tracked models. The shaded band is the instrument's measured noise range,
        published only once there are enough reads to establish one. Markers: C = content
        wave live, R = model refresh detected. View 1 indexes each series to its mean over
        the quiet pre-move weeks. The event study pools {data.eventStudy.series.length}{" "}
        illustrative events from {data.eventStudy.clients} clients.
      </p>
    </>
  );
}
